/**
 * KPI Templates admin page (SmartImprove).
 *
 * Consulting-grade reusable warehouse KPI templates aligned with the canonical
 * Business Process + Sub-Process taxonomy. The backend collection is still
 * named `smartimprove_kpi_library` (route slug `/kpi-library` preserved) for
 * backward compatibility — the user-facing label is "KPI Templates".
 *
 * Technical fields (slug, trend_direction, roi_relevance, benchmark_min/max,
 * target_value) remain in the DB but are MOSTLY hidden from the admin UX so
 * the screen feels like a consulting playbook, not master data maintenance.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  kpiLibraryApi,
  operationalAreasApi,
  subProcessesApi,
} from "../../lib/smartimproveAdminApi";

// ----- Canonical option sets (consultant-facing) -----
const KPI_CATEGORIES = [
  "Productivity",
  "Throughput",
  "Accuracy",
  "Quality",
  "Compliance",
  "Labor Efficiency",
  "Utilization",
  "SLA Adherence",
  "Visibility",
  "Automation Maturity",
  "Operational Stability",
];
const BUSINESS_IMPACT_AREAS = [
  "Productivity",
  "Cost Reduction",
  "Inventory Accuracy",
  "Customer Service",
  "Labor Optimization",
  "Warehouse Standardization",
  "Compliance",
  "Automation Readiness",
  "Operational Visibility",
];
const EXECUTIVE_IMPORTANCE = ["high", "medium", "low"];
const IMPROVEMENT_DIRECTION = [
  { value: "increase", label: "Increase" },
  { value: "reduce", label: "Reduce" },
  { value: "maintain", label: "Maintain" },
];

const IMPORTANCE_PILL = {
  high: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
  medium: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  low: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
};

function slugify(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

const EMPTY = {
  id: null,
  slug: "",
  name: "",
  operational_area: "",
  sub_process_key: "",
  kpi_category: "",
  unit: "",
  benchmark_min: "",
  benchmark_max: "",
  improvement_direction: "increase",
  typical_improvement_pct: "",
  business_impact_area: "",
  executive_importance: "medium",
  industry_applicability: [],
  description: "",
  active: true,
  sort_order: 0,
};

function deriveTrendDirection(improvement) {
  if (improvement === "increase") return "higher_is_better";
  if (improvement === "reduce") return "lower_is_better";
  return "neutral";
}

function formatBenchmark(min, max, unit) {
  const hasMin = min !== null && min !== undefined && min !== "";
  const hasMax = max !== null && max !== undefined && max !== "";
  if (!hasMin && !hasMax) return null;
  const fmt = (v) => (typeof v === "number" ? +Number(v).toFixed(2) : v);
  if (hasMin && hasMax) return `${fmt(min)} – ${fmt(max)} ${unit || ""}`.trim();
  if (hasMin) return `≥ ${fmt(min)} ${unit || ""}`.trim();
  return `≤ ${fmt(max)} ${unit || ""}`.trim();
}

function IndustryChips({ values }) {
  if (!values?.length) return <span className="text-[#9CA3AF]">—</span>;
  const visible = values.slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((v) => (
        <span
          key={v}
          className="px-1.5 py-0.5 text-[10px] rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] text-[#374151] capitalize"
        >
          {v.replace(/_/g, " ")}
        </span>
      ))}
      {values.length > visible.length ? (
        <span className="text-[10px] text-[#6B7280]">
          +{values.length - visible.length}
        </span>
      ) : null}
    </div>
  );
}

function KpiDialog({
  open,
  onOpenChange,
  value,
  processes,
  subProcesses,
  onChange,
  onSave,
  saving,
}) {
  const isEdit = !!value?.id;
  const subOptions = useMemo(
    () =>
      (subProcesses || [])
        .filter(
          (s) =>
            s.active !== false && s.process_key === value?.operational_area
        )
        .sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
            String(a.label).localeCompare(String(b.label))
        ),
    [subProcesses, value?.operational_area]
  );
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="kpi-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit KPI Template" : "New KPI Template"}
          </DialogTitle>
          <DialogDescription>
            Canonical warehouse KPI used during assessments, recommendations,
            ROI analysis, and executive reporting.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              KPI Template Name
            </label>
            <Input
              value={value?.name || ""}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="e.g. Picking productivity"
              className="rounded-sm text-xs"
              data-testid="kpi-field-name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Business Process
            </label>
            <Select
              value={value?.operational_area || ""}
              onValueChange={(v) =>
                set({ operational_area: v, sub_process_key: "" })
              }
            >
              <SelectTrigger className="rounded-sm text-xs" data-testid="kpi-field-process">
                <SelectValue placeholder="Select business process" />
              </SelectTrigger>
              <SelectContent>
                {processes
                  .filter((p) => p.active !== false)
                  .map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Sub-Process
            </label>
            <Select
              value={value?.sub_process_key || ""}
              onValueChange={(v) => set({ sub_process_key: v })}
              disabled={!value?.operational_area}
            >
              <SelectTrigger className="rounded-sm text-xs" data-testid="kpi-field-sub-process">
                <SelectValue
                  placeholder={
                    value?.operational_area
                      ? "Select sub-process (optional)"
                      : "Select business process first"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {subOptions.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              KPI Category
            </label>
            <Select
              value={value?.kpi_category || ""}
              onValueChange={(v) => set({ kpi_category: v })}
            >
              <SelectTrigger className="rounded-sm text-xs" data-testid="kpi-field-category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {KPI_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Unit of Measure
            </label>
            <Input
              value={value?.unit || ""}
              onChange={(e) => set({ unit: e.target.value })}
              placeholder="lines/hour, %, minutes, …"
              className="rounded-sm text-xs"
              data-testid="kpi-field-unit"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Benchmark Min
            </label>
            <Input
              value={value?.benchmark_min ?? ""}
              onChange={(e) => set({ benchmark_min: e.target.value })}
              placeholder="e.g. 60"
              className="rounded-sm text-xs"
              data-testid="kpi-field-benchmark-min"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Benchmark Max
            </label>
            <Input
              value={value?.benchmark_max ?? ""}
              onChange={(e) => set({ benchmark_max: e.target.value })}
              placeholder="e.g. 120"
              className="rounded-sm text-xs"
              data-testid="kpi-field-benchmark-max"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Improvement Direction
            </label>
            <Select
              value={value?.improvement_direction || "increase"}
              onValueChange={(v) => set({ improvement_direction: v })}
            >
              <SelectTrigger className="rounded-sm text-xs" data-testid="kpi-field-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {IMPROVEMENT_DIRECTION.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Typical Improvement %
            </label>
            <Input
              value={value?.typical_improvement_pct ?? ""}
              onChange={(e) =>
                set({ typical_improvement_pct: e.target.value })
              }
              placeholder="e.g. 25"
              className="rounded-sm text-xs"
              data-testid="kpi-field-improvement-pct"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Business Impact Area
            </label>
            <Select
              value={value?.business_impact_area || ""}
              onValueChange={(v) => set({ business_impact_area: v })}
            >
              <SelectTrigger className="rounded-sm text-xs" data-testid="kpi-field-impact-area">
                <SelectValue placeholder="Select impact area" />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_IMPACT_AREAS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Executive Importance
            </label>
            <Select
              value={value?.executive_importance || "medium"}
              onValueChange={(v) => set({ executive_importance: v })}
            >
              <SelectTrigger className="rounded-sm text-xs" data-testid="kpi-field-importance">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXECUTIVE_IMPORTANCE.map((c) => (
                  <SelectItem key={c} value={c} className="capitalize">
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Industry Applicability
            </label>
            <Input
              value={(value?.industry_applicability || []).join(", ")}
              onChange={(e) =>
                set({
                  industry_applicability: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              placeholder="all, pharma, food_beverage, retail …"
              className="rounded-sm text-xs"
              data-testid="kpi-field-industry"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Description
            </label>
            <Textarea
              value={value?.description || ""}
              onChange={(e) => set({ description: e.target.value })}
              rows={2}
              className="rounded-sm text-xs"
              data-testid="kpi-field-description"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="kpi-active"
              type="checkbox"
              checked={value?.active !== false}
              onChange={(e) => set({ active: e.target.checked })}
              data-testid="kpi-field-active"
            />
            <label htmlFor="kpi-active" className="text-xs text-[#374151]">
              Active
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Sort order
            </label>
            <Input
              type="number"
              value={value?.sort_order ?? 0}
              onChange={(e) => set({ sort_order: e.target.value })}
              className="rounded-sm text-xs"
              data-testid="kpi-field-sort-order"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs"
            data-testid="kpi-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving ||
              !value?.name?.trim() ||
              !value?.operational_area ||
              !value?.unit?.trim()
            }
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="kpi-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function KpiTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [subProcesses, setSubProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filterProcess, setFilterProcess] = useState("__all");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [items, bps, sps] = await Promise.all([
        kpiLibraryApi.list(),
        operationalAreasApi.list(),
        subProcessesApi.list(),
      ]);
      setRows(items || []);
      setProcesses(bps || []);
      setSubProcesses(sps || []);
    } catch (e) {
      toast.error("Failed to load KPI templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const processByKey = useMemo(() => {
    const m = {};
    processes.forEach((p) => (m[p.key] = p));
    return m;
  }, [processes]);
  const subByKey = useMemo(() => {
    const m = {};
    subProcesses.forEach((s) => (m[s.key] = s));
    return m;
  }, [subProcesses]);

  const sortedRows = useMemo(() => {
    const filtered =
      filterProcess === "__all"
        ? rows
        : rows.filter((r) => r.operational_area === filterProcess);
    return [...filtered].sort(
      (a, b) =>
        (processByKey[a.operational_area]?.sort_order ?? 999) -
          (processByKey[b.operational_area]?.sort_order ?? 999) ||
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.name).localeCompare(String(b.name))
    );
  }, [rows, filterProcess, processByKey]);

  const openCreate = () => {
    setForm({ ...EMPTY });
    setDialog(true);
  };
  const openEdit = (row) => {
    // Derive consultant-facing improvement_direction from legacy
    // trend_direction when the row predates the new field.
    const improvement =
      row.improvement_direction ||
      (row.trend_direction === "lower_is_better"
        ? "reduce"
        : row.trend_direction === "neutral"
        ? "maintain"
        : "increase");
    setForm({
      id: row.id,
      slug: row.slug || "",
      name: row.name || "",
      operational_area: row.operational_area || "",
      sub_process_key: row.sub_process_key || "",
      kpi_category: row.kpi_category || "",
      unit: row.unit || "",
      benchmark_min: row.benchmark_min ?? "",
      benchmark_max: row.benchmark_max ?? "",
      improvement_direction: improvement,
      typical_improvement_pct: row.typical_improvement_pct ?? "",
      business_impact_area: row.business_impact_area || "",
      executive_importance: row.executive_importance || "medium",
      industry_applicability: Array.isArray(row.industry_applicability)
        ? row.industry_applicability
        : [],
      description: row.description || "",
      active: row.active !== false,
      sort_order: row.sort_order ?? 0,
    });
    setDialog(true);
  };
  const toNumOrNull = (v) =>
    v === "" || v === null || v === undefined
      ? null
      : Number.isNaN(Number(v))
      ? v
      : Number(v);

  const save = async () => {
    const isEdit = !!form.id;
    const name = (form.name || "").trim();
    if (!name || !form.operational_area || !(form.unit || "").trim()) return;
    const slug = isEdit ? form.slug : slugify(name);
    const payload = {
      slug,
      name,
      operational_area: form.operational_area,
      sub_process_key: form.sub_process_key || "",
      kpi_category: form.kpi_category || "",
      unit: form.unit.trim(),
      benchmark_min: toNumOrNull(form.benchmark_min),
      benchmark_max: toNumOrNull(form.benchmark_max),
      improvement_direction: form.improvement_direction || "",
      trend_direction: deriveTrendDirection(form.improvement_direction),
      typical_improvement_pct: toNumOrNull(form.typical_improvement_pct),
      business_impact_area: form.business_impact_area || "",
      executive_importance: form.executive_importance || "",
      industry_applicability: form.industry_applicability || [],
      description: (form.description || "").trim(),
      active: !!form.active,
      sort_order: Number(form.sort_order) || 0,
    };
    setSaving(true);
    try {
      if (isEdit) await kpiLibraryApi.update(form.id, payload);
      else await kpiLibraryApi.create(payload);
      toast.success(`KPI template ${isEdit ? "updated" : "created"}`);
      setDialog(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await kpiLibraryApi.remove(row.id);
      toast.success("KPI template deleted");
      await reload();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-5" data-testid="si-kpi-library">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Intelligence Framework
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            KPI Templates
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            Reusable warehouse transformation KPI templates used across
            assessments, recommendations, ROI analysis, executive reporting,
            and transformation analytics.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="kpi-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add KPI Template
        </Button>
      </header>

      <div className="flex items-center gap-3">
        <label className="text-xs font-medium text-[#374151]">
          Filter by Business Process
        </label>
        <Select value={filterProcess} onValueChange={setFilterProcess}>
          <SelectTrigger
            className="rounded-sm text-xs w-72"
            data-testid="kpi-filter-process"
          >
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All Business Processes</SelectItem>
            {processes
              .filter((p) => p.active !== false)
              .map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#6B7280] ml-auto">
          {sortedRows.length} template{sortedRows.length === 1 ? "" : "s"}
        </span>
      </div>

      <section className="bg-white border border-[#E5E7EB] rounded-sm overflow-x-auto">
        {loading ? (
          <div className="px-5 py-8 text-sm text-[#6B7280]">Loading…</div>
        ) : sortedRows.length === 0 ? (
          <div
            className="px-5 py-12 text-center text-sm text-[#6B7280]"
            data-testid="kpi-empty"
          >
            No KPI templates yet. Click <strong>Add KPI Template</strong> to
            create the first one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-4 py-2 font-semibold">KPI Template</th>
                <th className="px-4 py-2 font-semibold">Business Process</th>
                <th className="px-4 py-2 font-semibold">Sub-Process</th>
                <th className="px-4 py-2 font-semibold">KPI Category</th>
                <th className="px-4 py-2 font-semibold">Unit</th>
                <th className="px-4 py-2 font-semibold">Benchmark</th>
                <th className="px-4 py-2 font-semibold">Improvement Target</th>
                <th className="px-4 py-2 font-semibold">Importance</th>
                <th className="px-4 py-2 font-semibold text-right">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => {
                const bp = processByKey[row.operational_area];
                const sp = subByKey[row.sub_process_key];
                const imp = String(row.executive_importance || "").toLowerCase();
                const direction = row.improvement_direction
                  ? row.improvement_direction
                  : row.trend_direction === "lower_is_better"
                  ? "reduce"
                  : row.trend_direction === "neutral"
                  ? "maintain"
                  : "increase";
                const benchmark = formatBenchmark(
                  row.benchmark_min,
                  row.benchmark_max,
                  row.unit
                );
                return (
                  <tr
                    key={row.id}
                    className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC] align-top"
                    data-testid={`kpi-row-${row.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-[#111827] font-medium">
                        {row.name}
                      </div>
                      {row.description ? (
                        <p className="text-[11px] text-[#6B7280] mt-0.5 line-clamp-2 max-w-md">
                          {row.description}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {bp?.label || <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {sp?.label || <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {row.kpi_category || (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {row.unit || <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-[#374151]">
                      {benchmark ? (
                        <span>{benchmark}</span>
                      ) : (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-[#374151]">
                      <span className="capitalize">{direction}</span>
                      {row.typical_improvement_pct ? (
                        <span className="text-[#025979] font-semibold ml-1">
                          {Number(row.typical_improvement_pct).toFixed(0)}%
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {imp ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm capitalize ${
                            IMPORTANCE_PILL[imp] ||
                            "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
                          }`}
                        >
                          {imp}
                        </span>
                      ) : (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.active === false ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
                          inactive
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(row)}
                        className="text-xs font-semibold text-[#025979] hover:text-[#03738C] mr-3"
                        data-testid={`kpi-edit-${row.id}`}
                        aria-label={`Edit ${row.name}`}
                      >
                        <Pencil className="inline w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(row)}
                        className="text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D]"
                        data-testid={`kpi-delete-${row.id}`}
                        aria-label={`Delete ${row.name}`}
                      >
                        <Trash2 className="inline w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <KpiDialog
        open={dialog}
        onOpenChange={setDialog}
        value={form}
        processes={processes}
        subProcesses={subProcesses}
        onChange={setForm}
        onSave={save}
        saving={saving}
      />
    </div>
  );
}
